import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Sun, CloudSun, Cloud, CloudFog, CloudRain, CloudSnow, CloudLightning, Drop, CheckCircle,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export const weatherIcon = (code, size = 22) => {
  const props = { size, weight: 'fill' };
  if (code === 0) return <Sun {...props} className="text-amber-500" />;
  if (code <= 2) return <CloudSun {...props} className="text-amber-400" />;
  if (code === 3) return <Cloud {...props} className="text-neutral-400" />;
  if (code <= 48) return <CloudFog {...props} className="text-neutral-400" />;
  if (code <= 67 || (code >= 80 && code <= 82)) return <CloudRain {...props} className="text-blue-500" />;
  if (code <= 77 || code === 85 || code === 86) return <CloudSnow {...props} className="text-sky-400" />;
  return <CloudLightning {...props} className="text-purple-500" />;
};

const dayLabel = (iso, i) => {
  if (i === 0) return 'Today';
  if (i === 1) return 'Tmrw';
  return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short' });
};

export const WeatherStrip = ({ location, days = 7, compact = false, showError = false }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!location) return;
    setData(null);
    setFailed(false);
    axios.get(`${API}/api/weather/forecast`, { params: { location, days } })
      .then((res) => setData(res.data))
      .catch(() => setFailed(true));
  }, [location, days]);

  if (!location) return null;
  if (failed) {
    return showError ? (
      <div className="flex items-center gap-2 text-xs font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg px-3 py-2.5" data-testid="weather-strip-error">
        <Cloud size={15} weight="fill" /> Couldn't find that location — try "City, ST" (e.g. Denver, CO)
      </div>
    ) : null;
  }
  if (!data) return <div className="h-16 bg-neutral-100 dark:bg-neutral-800 rounded-xl animate-pulse" data-testid="weather-strip-loading" />;

  return (
    <div data-testid="weather-strip">
      {!compact && (
        data.all_clear ? (
          <div className="flex items-center gap-2 text-xs font-bold text-emerald-700 dark:text-emerald-400 mb-2" data-testid="weather-all-clear">
            <CheckCircle size={15} weight="fill" /> No rain expected — clear week for outdoor work
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs font-bold text-blue-700 dark:text-blue-400 mb-2" data-testid="weather-rain-warning">
            <CloudRain size={15} weight="fill" /> Rain on {data.rain_days} day{data.rain_days === 1 ? '' : 's'} this week — plan indoor tasks
          </div>
        )
      )}
      <div className="grid grid-cols-7 gap-1.5">
        {data.forecast.slice(0, 7).map((d, i) => (
          <div
            key={d.date}
            title={`${d.condition} · ${d.precip_prob}% rain`}
            className={`flex flex-col items-center rounded-lg px-1 py-2 border ${d.will_rain ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700' : 'bg-neutral-50 dark:bg-neutral-900/40 border-neutral-200 dark:border-neutral-700'}`}
            data-testid={`weather-day-${d.date}`}
          >
            <span className="text-[9px] font-black uppercase text-neutral-500">{dayLabel(d.date, i)}</span>
            <span className="my-1">{weatherIcon(d.weather_code, compact ? 18 : 22)}</span>
            <span className="text-[10px] font-bold text-neutral-800 dark:text-neutral-200">{d.temp_max}°<span className="text-neutral-400">/{d.temp_min}°</span></span>
            {d.will_rain && (
              <span className="flex items-center gap-0.5 text-[9px] font-bold text-blue-600 dark:text-blue-400 mt-0.5">
                <Drop size={9} weight="fill" /> {d.precip_prob}%
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const WeatherPanel = ({ location: propLocation }) => {
  const [location, setLocation] = useState(propLocation || null);

  useEffect(() => {
    if (propLocation) { setLocation(propLocation); return; }
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setLocation(res.data?.user?.location || res.data?.location || null))
      .catch(() => {});
  }, [propLocation]);

  if (!location) return null;
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-[#0F172A] dark:border-neutral-700 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#2563EB] p-4 mb-6" data-testid="weather-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-neutral-900 dark:text-white flex items-center gap-2">
          <CloudSun size={20} weight="fill" className="text-amber-500" /> Job Weather
        </h3>
        <span className="text-xs text-neutral-500 font-medium">{location}</span>
      </div>
      <WeatherStrip location={location} />
    </div>
  );
};
